import { Injectable } from "@angular/core";
import L from "leaflet";
import { Subscription } from "rxjs";
import { MapViews, TBTabName } from "src/app/shared/entity/enums";
import { DataAccessService } from "src/app/shared/services/data-access/data-access.service";
import { DataRepositoryService } from "src/app/shared/services/data-repository/data-repository.service";
import { TaskInfoService } from "src/app/shared/services/task-info/task-info.service";

type CustomPolylineMarkerType = Partial<L.Polyline> & {
  uniqueID?: string;
  parentId?: string;
};

@Injectable({
  providedIn: "root",
})
export class MapLeafletService {
  markers: L.Marker[] = [];
  gpsSubscription: Subscription;
  map: any;
  blName: any;
  layerGroup: L.LayerGroup;
  gpsDataMap: Map<string, any> = new Map<string, any>();

  constructor(
    public dataRepositoryService: DataRepositoryService,
    public taskInfoService: TaskInfoService,
    private dataAccessService: DataAccessService
  ) {}

  

  /**
   * Checks if task movement is allowed within the specified marker.
   */
  getPolygonSelectedTaskInfo(insideMarkers: L.Marker<any>[]) {
    const selectedTaskInfo = this.getTasksInforForPolygon(insideMarkers);
    
    this.dataAccessService.setSelectedRowFromMap(selectedTaskInfo);
  }
  
  /**
   * Checks if task movement is allowed within the specified marker.
   */
  getPolygonDeselectedTaskInfo(insideMarkers: L.Marker<any>[]) {
    const deselectedTaskInfo = this.getTasksInforForPolygon(insideMarkers);
    
    this.dataAccessService.setDeselectedRowFromMap(deselectedTaskInfo);
  }

  getTasksInforForPolygon(taskList: L.Marker<any>[]):any[]{
    let reqTasksInfo: any[] = [];
    switch (this.taskInfoService.selectedTabName.toLowerCase()) {
      case TBTabName.Planned.toLowerCase():
        reqTasksInfo = this.getTaskInfoObject(taskList, "plannedTbCollection", "task_id");
        break;
      case TBTabName.Unplannedpickup.toLowerCase():
        reqTasksInfo = this.getTaskInfoObject(taskList, "unplannedTbCollection", "task_id");
        break;
      case TBTabName.Unloaded.toLowerCase():
        reqTasksInfo = this.getTaskInfoObject(taskList, "unloadedTbCollection", "item_id");
        break;
      case TBTabName.PlannedPBM.toLowerCase():
        reqTasksInfo = this.getTaskInfoObject(taskList, "plannedPbmTbCollection", "task_id");
        break;
      case TBTabName.PushToCloud.toLowerCase():
        reqTasksInfo = this.getTaskInfoObject(taskList, "cloudTbCollection", "task_id");
        break;
    }
    return reqTasksInfo;
  }

  getTaskInfoObject(taskList: L.Marker<any>[], dataRepoCollection: string, verifyId: string){
    let reqTasksInfo: any[] = [];
    taskList.forEach((marker: any) => {
      reqTasksInfo.push(
        [...this.dataRepositoryService[dataRepoCollection].values()].find(
          (movingTask: any) => movingTask[verifyId] === marker.uniqueID
        )
      );
    });
    return reqTasksInfo;
  }

  /**
   * Determines if a given marker is inside a polygon.
   */
  isMarkerInsidePolygon(marker: L.LatLng, polygon: L.LatLng[]) {
    const markerLatitude = marker.lat;
    const markerLongitude = marker.lng;
    let isInside = false;

    polygon.forEach((currentMarker, currentIndex) => {
      const previousIndex =
        currentIndex === 0 ? polygon.length - 1 : currentIndex - 1;
      const currentMarkerLatitude = currentMarker.lat;
      const currentMarkerLongitude = currentMarker.lng;
      const previousMarkerLatitude = polygon[previousIndex].lat;

      const previousMarkerLongitude = polygon[previousIndex].lng;
      const doesIntersect =
        currentMarkerLongitude > markerLongitude !==
          previousMarkerLongitude > markerLongitude &&
        markerLatitude <
          ((previousMarkerLatitude - currentMarkerLatitude) *
            (markerLongitude - currentMarkerLongitude)) /
            (previousMarkerLongitude - currentMarkerLongitude) +
            currentMarkerLatitude;

      if (doesIntersect) {
        isInside = !isInside;
      }
    });
    return isInside;
  }

  /**
   * Checks if GPS updates are allowed based on date offset and GPS subscription status.
   */
  validateGPSActivation() {
    return (
      this.dataRepositoryService.dateOffset === 0 &&
      (this.gpsSubscription?.closed || !this.gpsSubscription)
    );
  }
  /**
   * Clears the color mapping for routes.
   */
  clearRoutesColorMap() {
    this.dataRepositoryService.routesColour?.clear();
  }

  /**
   * Unsubscribes from GPS updates if the GPS subscription is active and date offset is zero.
   */
  clearGPSSubscription() {
    if (
      this.dataRepositoryService.dateOffset === 0 &&
      Boolean(this.gpsSubscription)
    ) {
      this.gpsSubscription.unsubscribe();
    }
  }
  
  /**
   * Retrives markers inside a given polygon layer and validates their movement.
   * @param polygonLayer layer which may cover marker inside it
   * @returns list of marker on map coming inside polygon area
   */
  getFeaturesInsidePolygon(polygonLayer: any): L.Marker[] {
    const polygon = polygonLayer.getLatLngs()[0];

    const insideMarkers: L.Marker[] = [];

    this.markers.forEach((marker: any) => {
      const markerLatLng = marker.getLatLng();
      if (this.isMarkerInsidePolygon(markerLatLng, polygon)) {
        insideMarkers.push(marker);
      }
    });
    return insideMarkers;
  }

  /**
   * Method groups the input data collection by key  
   * @param data data object
   * @param key key property for grouping 
   * @returns returns the grouped object
   */
  groupCollection(data: any, key: any) {
    let res: any;
    res = data.reduce((rv: any, x: any) => {
      let v = key instanceof Function ? key(x) : x[key];
      let el = rv.find((r: any) => r && r.key === v);
      if (el) {
        el.values.push(x);
      } else {
        rv.push({ key: v, values: [x] });
      }
      return rv;
    }, []);

    return res;
  }

  

}
